import React, { useCallback, useState } from "react";
import { api, API, downloadCsv } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Download, PlugZap, RefreshCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SafetyCulture() {
  const fetcher = useCallback(() => api.get("/integrations/safetyculture").then((r) => r.data), []);
  const { data, loading, reload } = useFetch(fetcher);
  const [form, setForm] = useState({ api_token: "", asset_type_id: "", site_id: "", notes: "" });
  const [busy, setBusy] = useState(null);

  React.useEffect(() => {
    if (data) {
      setForm((f) => ({
        ...f,
        asset_type_id: data.asset_type_id || "",
        site_id: data.site_id || "",
        notes: data.notes || "",
      }));
    }
  }, [data]);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/integrations/safetyculture", form);
      toast.success("SafetyCulture settings saved");
      setForm((f) => ({ ...f, api_token: "" }));
      reload();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data: result } = await api.post("/integrations/safetyculture/test");
      toast.success(result.message || "Connection successful");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(null);
    }
  };

  const syncTools = async () => {
    if (!window.confirm("Sync all HAVS tools to SafetyCulture Assets? Existing duplicate asset codes may be rejected by SafetyCulture.")) return;
    setBusy("sync");
    try {
      const { data: result } = await api.post("/integrations/safetyculture/sync-tools", { dry_run: false });
      toast.success(`Synced ${result.synced}; failed ${result.failed}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(null);
    }
  };

  const downloadImportCsv = async () => {
    setBusy("csv");
    try {
      await downloadCsv(`${API}/integrations/safetyculture/tools-import.csv`, "safetyculture_tools_import.csv");
      toast.success("SafetyCulture CSV downloaded");
    } catch (err) {
      console.error(err);
      toast.error("CSV download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="safetyculture-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Integration</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
          SafetyCulture
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Connect HAVS Guard to SafetyCulture so your tool register can be exported or pushed into SafetyCulture Assets.
        </p>
      </div>

      <Card className="p-6 border-slate-200 max-w-3xl space-y-5">
        <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <ShieldCheck className="h-5 w-5 text-slate-700 mt-0.5" />
          <div className="text-sm text-slate-700">
            <p className="font-bold text-slate-900">Status</p>
            {loading ? (
              <p>Loading…</p>
            ) : data?.connected ? (
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Connected: {data.token_preview}</p>
            ) : (
              <p>Not connected yet. Add a SafetyCulture API token below.</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>SafetyCulture API token</Label>
            <Input
              type="password"
              value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              placeholder={data?.connected ? "Leave blank to keep saved token" : "Paste token here"}
              data-testid="sc-token-input"
            />
            <p className="text-xs text-slate-500">
              Create a personal/service account API token inside SafetyCulture, then paste it here. The backend stores it server-side.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Asset Type ID</Label>
            <Input
              value={form.asset_type_id}
              onChange={(e) => setForm({ ...form, asset_type_id: e.target.value })}
              placeholder="Required for direct asset sync"
              data-testid="sc-asset-type-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Site ID</Label>
            <Input
              value={form.site_id}
              onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              placeholder="Optional"
              data-testid="sc-site-input"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Example: Tool assets imported to SafetyCulture HAVS Tools asset type."
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy !== null} className="bg-slate-900 hover:bg-slate-800">
            {busy === "save" ? "Saving…" : "Save settings"}
          </Button>
          <Button variant="outline" onClick={test} disabled={busy !== null || !data?.connected} className="gap-2">
            <PlugZap className="h-4 w-4" /> Test connection
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 max-w-3xl">
        <h2 className="font-display text-xl font-bold text-slate-900">Tool register export</h2>
        <p className="text-sm text-slate-600 mt-1 mb-4">
          Download a SafetyCulture-ready CSV for manual import, or push your tools into SafetyCulture Assets using the API.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadImportCsv} disabled={busy !== null} className="gap-2">
            <Download className="h-4 w-4" /> {busy === "csv" ? "Preparing…" : "Download import CSV"}
          </Button>
          <Button onClick={syncTools} disabled={busy !== null || !data?.connected} className="gap-2 bg-slate-900 hover:bg-slate-800">
            <RefreshCcw className="h-4 w-4" /> {busy === "sync" ? "Syncing…" : "Sync tools to Assets"}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Direct sync needs a valid SafetyCulture Asset Type ID. If your organisation uses custom asset fields, use the CSV first, then map fields in SafetyCulture.
        </p>
      </Card>
    </div>
  );
}
